import { Suspense, lazy } from "react";
import { Route, Routes, Navigate, Outlet, Link, useLocation } from "react-router-dom";
import { Loading } from "./common/UI/Loading";
import { observer, inject } from "mobx-react";
import { isMobileDevice, isTabletDevice } from "./utils/device";

const Login = lazy(() => import("./pages/Login"));
const Register = lazy(() => import("./pages/Register"));
const TransmitOrder = lazy(() => import("./pages/TransmitOrder"));
const Catalog = lazy(() => import("./pages/Catalog"));
const Cart = lazy(() => import("./pages/Cart"));
const RegistrationComplete = lazy(() => import("./pages/RegistrationComplete"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const ProductPage = lazy(() => import("./pages/ProductPage"));
const Iframe = lazy(() => import("./components/Iframe"));
const NewProject = lazy(() => import("./pages/NewProject"));
const UpdateProject = lazy(() => import("./pages/UpdateProject"));
const DeliveryAppLogin = lazy(() => import("./components/DeliveryOrder/Login"));
const Lists = lazy(() => import("./components/DeliveryOrder/Lists"));
const FramesList = lazy(() => import("./components/DeliveryOrder/FramesList"));
const OrdersList = lazy(() => import("./components/DeliveryOrder/OrdersList"));
const SupplyOrderDetails = lazy(() => import("./components/DeliveryOrder/SupplyOrderDetails"));
const FrameOrderDetails = lazy(() => import("./components/DeliveryOrder/FrameOrderDetails"));
const FrameOrderItem = lazy(() => import("./components/DeliveryOrder/FrameOrderItem"));
const MobileCart = lazy(() => import("./components/DeliveryOrder/Cart"));
const OpenOnMobilePage = lazy(() => import("./components/DeliveryOrder/OpenOnMobilePage"));

const ProtectedRoute = ({ isAllowed, redirectPath = "/login", children }) => {
  const location = useLocation();
  if (!isAllowed) {
    const to =
      redirectPath === "/login"
        ? { pathname: "/login", state: { from: location } }
        : redirectPath;
    return <Navigate to={to} replace />;
  }
  return children ? children : <Outlet />;
};

const AppRoutes = ({ userStore }) => {
  const isTablet = isTabletDevice();
  const isMobile = isMobileDevice();

  return (
    <Suspense fallback={<Loading />}>
      <Routes>
        <Route element={<ProtectedRoute isAllowed={userStore.isAppAuthenticated} redirectPath="/delivery_order/login" />}>
          <Route
            path="/delivery_order"
            element={<Navigate to="/delivery_order/main" />}
          />
          <Route
            path="/delivery_order/main"
            element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <Lists />}
          />
          <Route
            path="/delivery_order/frameList"
            element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <FramesList />}
          />
          <Route
            path="/delivery_order/ordersList"
            element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <OrdersList />}
          />
          <Route
            path="/delivery_order/cart"
            element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <MobileCart />}
          />
          <Route exact path="/delivery_order/supply_order_details">
            <Route
              index
              element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <SupplyOrderDetails />}
            />
            <Route
              path=":orderDocno"
              element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <SupplyOrderDetails />}
            />
          </Route>
          <Route exact path="/delivery_order/frame_order_details">
            <Route
              index
              element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <FrameOrderDetails />}
            />
            <Route
              path=":orderDocno"
              element={
                !isMobile && !isTablet ? <OpenOnMobilePage /> : <FrameOrderDetails />
              }
            />
          </Route>
          <Route exact path="/delivery_order/frame_order_item">
            <Route
              index
              element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <FrameOrderItem />}
            />
            <Route
              path=":productId"
              element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <FrameOrderItem />}
            />
          </Route>
        </Route>
        <Route element={<ProtectedRoute isAllowed={userStore.isAuthenticated} />}>
          <Route path="/" exact element={<Navigate exact from="/" to="/catalog" />} />
          <Route path="/catalog" element={<Outlet />}>
            <Route index element={<Catalog />} />
            <Route path=":productId" element={<ProductPage />} />
          </Route>
          <Route key="cart" path="/cart" element={<Cart />} />
          <Route key="myOrders" path="/my-orders" element={<MyOrders />} />
          <Route key="UpdateProject" path="/update-project" element={<UpdateProject />} />
          <Route key="myOrders" path="/my-orders" element={<MyOrders />} />
          <Route key="projectDetails" path="/new-project" element={<NewProject />} />
          <Route key="transmitOrder" path="/transmit-order" element={<TransmitOrder />} />
        </Route>
        <Route
          element={
            <ProtectedRoute redirectPath="/catalog" isAllowed={!userStore.isAuthenticated} />
          }
        >
          <Route exact key="login" path="/login" element={<Login />} />
          <Route exact key="register" path="/register" element={<Register />} />
        </Route>
        <Route
          element={
            <ProtectedRoute
              redirectPath="/delivery_order/main"
              isAllowed={!userStore.isAppAuthenticated}
            />
          }
        >
          <Route
            path="/delivery_order/login"
            element={!isMobile && !isTablet ? <OpenOnMobilePage /> : <DeliveryAppLogin />}
          />
        </Route>
        <Route
          exact
          key="RegistrationComplete"
          path="/registration-completed"
          element={<RegistrationComplete />}
        />

        {/* <Route path="/blog" element={<Iframe page="blog" />} />
        <Route path="/about-us" element={<Iframe page="aboutUs" />} /> */}
        <Route path="/colors-catalog" element={<Iframe page="colorsCatalog" />} />
        <Route path="/video" element={<Iframe page="video" />} />
        <Route exact key="myOrders" path="/my-orders" element={<MyOrders />} />
        <Route path="/" element={<Navigate exact from="/" to="/login" />} />
      </Routes>
      <Link to={"/register"} style={{ display: "none" }}>
        linkToRegister
      </Link>
      <Link to={"/cart"} style={{ display: "none" }}>
        linkToCart
      </Link>
      <Link to={"/my-orders"} style={{ display: "none" }}>
        linkToMyOrders
      </Link>
      <Link to={"/transmit-order"} style={{ display: "none" }}>
        linkToTransmitOrder
      </Link>
      <Link to={"/update-project"} style={{ display: "none" }}>
        linkToSelectProject
      </Link>
      <Link to={"/new-project"} style={{ display: "none" }}>
        linkToProjectDetails
      </Link>
      <Link to={"/registration-completed"} style={{ display: "none" }}>
        registrationCompleted
      </Link>
      <Link to={"/login"} style={{ display: "none" }}>
        linkToLogin
      </Link>
      <Link to={"delivery_order/login"} style={{ display: "none" }}>
        linkToLogin
      </Link>
      <Link to={"delivery_order/main"} style={{ display: "none" }}>
        linkToLogin
      </Link>
      <Link to={"delivery_order/frameList"} style={{ display: "none" }}>
        linkToLogin
      </Link>
      <Link to={"/catalog"} style={{ display: "none" }}>
        linkToCatalog
      </Link>
    </Suspense>
  );
};

export default inject("userStore")(observer(AppRoutes));
